type LiveListener<EventType extends keyof DocumentEventMap> = (
  event: DocumentEventMap[EventType],
  currentTarget: Element,
) => void;

type LiveStore<EventType extends keyof DocumentEventMap> = Map<
  EventType,
  LiveEventMap<EventType>
>;

type LiveEventMap<EventType extends keyof DocumentEventMap> = Map<
  string,
  LiveEventSet<EventType>
>;

type LiveEventSet<EventType extends keyof DocumentEventMap> = Set<
  LiveListener<EventType>
>;

const store = new Map();

const documentStore = new Map<keyof DocumentEventMap, (event: Event) => void>();

const getStore = <EventType extends keyof DocumentEventMap>() =>
  store as LiveStore<EventType>;

const getDocumentStore = <EventType extends keyof DocumentEventMap>() =>
  documentStore as Map<
    keyof DocumentEventMap,
    (event: DocumentEventMap[EventType]) => void
  >;

export const addLiveEventListener = <EventType extends keyof DocumentEventMap>(
  type: EventType,
  selector: string,
  listener: LiveListener<EventType>,
) => {
  const store = getStore<EventType>();
  const eventListeners =
    store.get(type) ?? (new Map() as LiveEventMap<EventType>);
  const listeners = eventListeners.get(selector) ?? new Set();

  listeners.add(listener);
  eventListeners.set(selector, listeners);
  store.set(type, eventListeners);

  updateDocumentEventListeners(type);
};

export const removeLiveEventListener = <
  EventType extends keyof DocumentEventMap,
>(
  type: EventType,
  selector: string,
  listener: LiveListener<EventType>,
) => {
  const store = getStore<EventType>();

  const listeners = store.get(type)?.get(selector);

  listeners?.delete(listener);

  updateDocumentEventListeners(type);
};

export const live = addLiveEventListener;

const updateDocumentEventListeners = <EventType extends keyof DocumentEventMap>(
  type: EventType,
) => {
  const documentStore = getDocumentStore<EventType>();
  const listener = documentStore.get(type);
  const eventListeners = getStore<EventType>().get(type);
  const active = Array.from(eventListeners?.values() ?? []).some(
    (set) => set.size > 0,
  );

  if (active === true && listener === undefined) {
    const listener = (event: DocumentEventMap[EventType]) => {
      dispatchEvent(type, event);
    };
    documentStore.set(type, listener);
    document.addEventListener(type, listener);
  } else if (active === false && listener !== undefined) {
    documentStore.delete(type);
    document.removeEventListener(type, listener);
  }
};

const dispatchEvent = <EventType extends keyof DocumentEventMap>(
  type: EventType,
  event: DocumentEventMap[EventType],
) => {
  const target = event.target;

  if (target instanceof Element === false) {
    return;
  }

  const store = getStore<EventType>();
  const listenersEntries = Array.from(store.get(type)?.entries() ?? []);

  for (const [selector, listeners] of listenersEntries) {
    const currentTarget = target.closest(selector);

    if (currentTarget !== null) {
      for (const listener of listeners) {
        listener(event, currentTarget);
      }
    }
  }
};
